import { useCallback, useEffect, useState } from 'react';

import { toast } from 'sonner';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { useGetFileHistoryStats } from '@/api/queries/useGetFileHistoryStats';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { DeletionHistory } from '@/api/types/backend-dto-types';
import { SkippedFile, ProcessedDoc, NotProcessedDoc, File } from '@/api/types/backend-dto-types';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import { downloadUploadOverviewReport } from '@/helpers/report-utils';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SkippedFilesExplainerModal } from '@/new/pages/matters/_matterId/settings/file-history/components/modals/skipped-files-explainer-modal';
import { UploadFlowChart } from '@/new/pages/matters/_matterId/settings/file-history/components/upload-flow-chart';
import { DEFAULT_STATS } from '@/new/pages/matters/_matterId/settings/file-history/utils';

interface UploadBreakdownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
}

export function UploadBreakdownModal({ open, onOpenChange, matterId }: UploadBreakdownModalProps) {
  const { data: stats, isLoading, refetch } = useGetFileHistoryStats(matterId);
  const { data: matter } = useGetMatterV2(matterId);
  const resolvedStats = stats ?? DEFAULT_STATS;
  const hasRelativityEnabled = !!matter?.relativityWorkspaceId;

  // Always refetch file history data on open
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  const [isDownloading, setIsDownloading] = useState(false);
  const [skippedFilesExplainerModalOpen, setSkippedFilesExplainerModalOpen] = useState(false);
  const { fetchConfigGET } = useGetFetchConfig();

  const handleDownloadReport = useCallback(async () => {
    if (!matterId || resolvedStats.totalAttempted === 0) return;

    setIsDownloading(true);
    try {
      trackCustomEvent(TrackingEventsV2.FileHistoryDownloadOverviewReport, { matterId });

      const [skippedRes, deletedRes, awaitingRes, processedRes, notProcessedRes] = await Promise.all([
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/skipped`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/deleted`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/files`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/processed`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/not-processed`, fetchConfigGET),
      ]);

      const [skippedFiles, deletedFiles, awaitingFiles, processedDocs, notProcessedDocs] = await Promise.all([
        skippedRes.json() as Promise<SkippedFile[]>,
        deletedRes.json() as Promise<DeletionHistory[]>,
        awaitingRes.json() as Promise<File[]>,
        processedRes.json() as Promise<ProcessedDoc[]>,
        notProcessedRes.json() as Promise<NotProcessedDoc[]>,
      ]);

      downloadUploadOverviewReport(
        {
          stats: resolvedStats,
          skippedFiles,
          deletedFiles,
          awaitingFiles,
          processedDocs,
          notProcessedDocs,
        },
        matterId,
        { includeControlNumber: hasRelativityEnabled },
      );
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setIsDownloading(false);
    }
  }, [resolvedStats, matterId, fetchConfigGET, hasRelativityEnabled]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--git-branch h-4.5 w-4.5 text-blue-500" />
            Upload Flow
          </DialogTitle>
          <DialogDescription>See how your files flow through the upload and processing pipeline</DialogDescription>
        </DialogHeader>

        <div>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Skeleton className="h-48 w-full" />
            </div>
          ) : stats ? (
            <UploadFlowChart matterId={matterId} stats={stats} linksEnabled={false} />
          ) : (
            <div className="flex h-64 flex-col items-center justify-center text-gray-400">
              <Icon className="lucide--git-branch h-10 w-10 opacity-40" />
              <p className="mt-2 text-sm">No upload data yet</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setSkippedFilesExplainerModalOpen(true);
              trackCustomEvent(TrackingEventsV2.FileHistoryOpenSkippedExplainer, { matterId });
            }}
          >
            <Icon className="lucide--info h-3.5 w-3.5" />
            Why are files skipped?
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="default" size="sm" onClick={handleDownloadReport} disabled={isDownloading}>
                {isDownloading ? (
                  <Icon className="lucide--loader-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Icon className="lucide--download h-3.5 w-3.5" />
                )}
                {isDownloading ? 'Downloading...' : 'Download Report'}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm p-0" sideOffset={8}>
              <div className="p-3">
                <p className="mb-3 text-xs font-medium text-gray-900">
                  Downloads an Excel (.xlsx) report with multiple sheets.
                </p>
                <div className="flex items-center justify-center rounded border bg-white">
                  <div className="relative mt-2 flex w-full justify-center border-t border-gray-300 bg-gray-100 pb-2">
                    <div className="relative z-[4] -mt-px inline-flex items-center rounded-b-md border border-gray-300 border-t-white bg-white px-2.5 py-1 text-xs font-medium text-green-700">
                      Overview
                    </div>
                    <div className="relative z-[3] -ml-[2px] inline-flex items-center border-r border-l-0 border-gray-300 bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                      Skipped
                    </div>
                    <div className="relative z-[2] -ml-[2px] inline-flex items-center border-r border-l-0 border-gray-300 bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                      Deleted
                    </div>
                    <div className="relative z-[1] -ml-[2px] inline-flex items-center border-r border-l-0 border-gray-300 bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                      Processed
                    </div>
                  </div>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
        <SkippedFilesExplainerModal
          open={skippedFilesExplainerModalOpen}
          onOpenChange={setSkippedFilesExplainerModalOpen}
        />
      </DialogContent>
    </Dialog>
  );
}
